import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Building2, Mail, Phone, FileSearch, RefreshCw } from "lucide-react";

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [migrating, setMigrating] = useState(false);
  const navigate = useNavigate();

  const migrateResend = async () => {
    if (!confirm("Adicionar TODOS os contatos do CRM à Audience do Resend (como inscritos)?")) return;
    setMigrating(true);
    try {
      const { data } = await api.post("/clients/resend-migrate");
      toast.success(`Sincronizados ${data.contacts_synced} contato(s) com o Resend`);
    } catch (e) {
      toast.error("Falha ao sincronizar com o Resend");
    } finally {
      setMigrating(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: q ? { q } : {} });
      setClients(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div className="max-w-7xl mx-auto fade-up" data-testid="clients-page">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2">Banco de Dados</div>
          <h1 className="font-display text-4xl font-black text-zinc-50 tracking-tighter">Clientes</h1>
          <p className="text-zinc-500 mt-2 text-sm">Gerencie cadastros, logos e histórico comercial.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={migrateResend}
            disabled={migrating}
            data-testid="resend-migrate-btn"
            title="Sincronizar todos os contatos com o Resend"
            className="flex items-center gap-2 px-4 py-3 bg-transparent border border-zinc-800 hover:border-zinc-600 text-zinc-300 font-bold rounded-lg text-sm transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${migrating ? "animate-spin" : ""}`} strokeWidth={2} />
            {migrating ? "Sincronizando..." : "Sincronizar Resend"}
          </button>
          <button
            onClick={() => navigate("/clientes/novo")}
            data-testid="new-client-btn"
            className="flex items-center gap-2 px-5 py-3 bg-zinc-100 hover:bg-white text-zinc-900 font-bold rounded-lg text-sm transition-all"
          >
            <Plus className="w-4 h-4" strokeWidth={2.2} /> Novo Cliente
          </button>
        </div>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" strokeWidth={1.5} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="clients-search-input"
          placeholder="Buscar por nome, NIF, e-mail..."
          className="w-full pl-10 pr-3 py-2.5 bg-[#0e0e10] border border-zinc-800 rounded-lg text-zinc-100 placeholder-zinc-600 focus:border-zinc-500 focus:outline-none text-sm"
        />
      </div>

      {loading && <div className="text-zinc-500">Carregando...</div>}
      {!loading && clients.length === 0 && (
        <div className="rounded-xl border border-dashed border-zinc-800 bg-[#0e0e10]/40 p-12 text-center" data-testid="clients-empty-state">
          <Building2 className="w-10 h-10 text-zinc-700 mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-zinc-300 font-medium mb-1">Nenhum cliente cadastrado</div>
          <div className="text-zinc-500 text-sm mb-5">Comece adicionando seu primeiro cliente.</div>
          <button
            onClick={() => navigate("/clientes/novo")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-100 hover:bg-white text-zinc-900 font-bold rounded-lg text-sm"
          >
            <Plus className="w-4 h-4" /> Adicionar Cliente
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {clients.map((c) => (
          <div
            key={c.id}
            onClick={() => navigate(`/clientes/${c.id}`)}
            data-testid={`client-card-${c.id}`}
            className="group rounded-xl border border-zinc-800 bg-[#121214] p-5 hover:-translate-y-0.5 hover:border-zinc-600 hover:shadow-[0_8px_30px_rgba(0,0,0,0.5)] transition-all cursor-pointer"
          >
            <div className="flex items-start gap-4 mb-3">
              <div className="w-12 h-12 rounded-lg bg-zinc-900 border border-zinc-800 overflow-hidden flex items-center justify-center shrink-0">
                {c.logo_path ? (
                  <img src={fileUrl(c.logo_path)} alt="" className="w-full h-full object-contain" />
                ) : (
                  <Building2 className="w-5 h-5 text-zinc-600" strokeWidth={1.5} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-zinc-100 truncate">{c.name}</div>
                {c.nif && <div className="text-xs text-zinc-500 font-mono">NIF {c.nif}</div>}
              </div>
              <FileSearch className="w-4 h-4 text-zinc-700 group-hover:text-zinc-400 transition-colors" strokeWidth={1.5} />
            </div>
            <div className="space-y-1 text-xs text-zinc-500">
              {c.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3" /> {c.email}</div>}
              {c.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" /> {c.phone}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Clients;
